<script setup lang="ts">
const { t, locale, locales } = useI18n()
const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()
const route = useRoute()

const availableLocales = computed(() => {
  return locales.value.filter((l: any) => l.code !== locale.value)
})

const currentLocale = computed(() => {
  return locales.value.find((l: any) => l.code === locale.value)
})

// Main navigation with icons
const navItems = computed(() => [
  {
    label: t('nav.home'),
    to: localePath('/'),
    icon: 'i-lucide-house',
    description: t('header.homeDesc')
  },
  {
    label: t('nav.shop'),
    to: localePath('/shop'),
    icon: 'i-lucide-shopping-bag',
    description: t('header.shopDesc')
  },
  {
    label: t('nav.restoration'),
    to: localePath('/restauracion'),
    icon: 'i-lucide-wrench',
    description: t('header.repairDesc')
  },
  {
    label: t('nav.weBuy'),
    to: localePath('/compramos'),
    icon: 'i-lucide-hand-coins',
    description: t('header.sellDesc')
  },
  {
    label: t('nav.howItWorks'),
    to: `${localePath('/')}#how-it-works`,
    icon: 'i-lucide-route',
    description: t('header.howItWorksDesc')
  },
  {
    label: t('nav.affiliate'),
    to: localePath('/cuenta/afiliados'),
    icon: 'i-lucide-share-2',
    description: t('header.affiliateDesc')
  },
  {
    label: t('nav.blog'),
    to: localePath('/blog'),
    icon: 'i-lucide-newspaper',
    description: t('header.blogDesc')
  }
])

const languageItems = computed(() =>
  availableLocales.value.map((loc: any) => ({
    label: loc.name,
    to: switchLocalePath(loc.code)
  }))
)

const { loginRoute, registerRoute } = useLoginRedirect()
const { itemCount: cartCount, fetchCart, resetCart } = useCartAPI()
const mobileMenuOpen = ref(false)
const searchOpen = ref(false)
const searchQuery = ref('')
const menuButtonRef = ref<HTMLButtonElement | null>(null)
const drawerRef = ref<HTMLElement | null>(null)
const drawerCloseRef = ref<HTMLButtonElement | null>(null)
const desktopSearchRef = ref<HTMLInputElement | null>(null)
const mobileSearchRef = ref<HTMLInputElement | null>(null)
let previousBodyOverflow = ''

function handleSearch() {
  const q = searchQuery.value.trim()
  if (!q) return
  navigateTo(localePath(`/shop?q=${encodeURIComponent(q)}`))
  searchQuery.value = ''
  searchOpen.value = false
}

function closeMobileMenu(restoreFocus = true) {
  mobileMenuOpen.value = false
  if (restoreFocus) nextTick(() => menuButtonRef.value?.focus())
}

function handleGlobalShortcut(event: KeyboardEvent) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    if (window.innerWidth >= 1024) desktopSearchRef.value?.focus()
    else {
      searchOpen.value = true
      nextTick(() => mobileSearchRef.value?.focus())
    }
  }
}

function handleDrawerKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault()
    closeMobileMenu()
    return
  }
  if (event.key !== 'Tab' || !drawerRef.value) return
  const focusable = Array.from(drawerRef.value.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )).filter(element => !element.hasAttribute('hidden'))
  if (focusable.length === 0) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last?.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first?.focus()
  }
}

// Auth (state is restored by auth plugin, no need to call initAuth here)
const { user, isLoggedIn, logout } = useAuth()

// Fetch cart once auth state is ready
onMounted(() => {
  if (isLoggedIn.value) fetchCart()
  window.addEventListener('keydown', handleGlobalShortcut)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleGlobalShortcut)
  document.body.style.overflow = previousBodyOverflow
})
watch(isLoggedIn, (val) => {
  if (val) fetchCart()
  else resetCart()
})

const userMenuItems = computed(() => [
  {
    label: t('nav.myAccount'),
    icon: 'i-lucide-user',
    to: localePath('/cuenta/profile')
  },
  {
    label: t('nav.myOrders'),
    icon: 'i-lucide-package',
    to: localePath('/cuenta/orders')
  },
  {
    label: t('nav.logout'),
    icon: 'i-lucide-log-out',
    onSelect: () => {
      logout()
      navigateTo(localePath('/'))
    }
  }
])

// Check if current route is active
const isActive = (path: string) => {
  const homePath = localePath('/')
  // For home page, exact match only
  if (path === homePath) {
    return route.path === path
  }
  // For shop, also match product detail pages. We can't use
  // localePath('/producto') here because the router only has a
  // /producto/[slug] route; resolving the bare path triggers a
  // "No match found for location with path '/producto'" warning
  // on every header render. Match against the `/producto/` segment
  // directly — present in the URL across every locale prefix.
  const shopPath = localePath('/shop')
  if (path === shopPath) {
    return route.path.startsWith(path) || route.path.includes('/producto/')
  }
  return route.path.startsWith(path)
}

// Close mobile menu on route change
watch(() => route.path, () => {
  closeMobileMenu(false)
  searchOpen.value = false
})

watch(mobileMenuOpen, open => {
  if (!import.meta.client) return
  if (open) {
    previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    nextTick(() => drawerCloseRef.value?.focus())
  } else {
    document.body.style.overflow = previousBodyOverflow
  }
})
</script>

<template>
  <header class="relative z-40">
    <!-- Top Bar - Promotional message -->
    <div class="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 py-2 px-4 text-center text-xs sm:text-sm">
      <div class="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-gray-600 dark:text-gray-300">
        <span class="flex items-center gap-1.5 font-semibold text-brand-700 dark:text-brand-300">
          <UIcon name="i-lucide-credit-card" aria-hidden="true" class="size-4 shrink-0" />
          {{ t('financing.noFees') }}
        </span>
        <span class="flex items-center gap-1.5">
          <UIcon name="i-lucide-truck" aria-hidden="true" class="size-4 shrink-0 text-brand-600 dark:text-brand-400" />
          {{ t('header.topBar.freeShipping') }}
        </span>
        <span class="hidden sm:flex items-center gap-1.5">
          <UIcon name="i-lucide-shield-check" aria-hidden="true" class="size-4 shrink-0 text-brand-600 dark:text-brand-400" />
          {{ t('header.topBar.warranty') }}
        </span>
        <span class="hidden md:flex items-center gap-1.5">
          <UIcon name="i-lucide-rotate-ccw" aria-hidden="true" class="size-4 shrink-0 text-brand-600 dark:text-brand-400" />
          {{ t('header.topBar.returns') }}
        </span>
      </div>
    </div>

    <!-- Main Header -->
    <div class="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 shadow-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16 lg:h-20">
          <!-- Logo -->
          <NuxtLink :to="localePath('/')" aria-label="MiDiGi.NET" class="flex flex-shrink-0 items-center gap-1.5">
            <img src="/logo.svg" alt="" width="192" height="192" class="h-5 w-auto sm:h-6 lg:h-7" />
            <img src="/logo-text.svg" alt="" width="230" height="44" class="h-5 w-auto dark:invert sm:h-6 lg:h-7" />
          </NuxtLink>

          <!-- Search Bar (Desktop) -->
          <div class="hidden lg:flex flex-1 max-w-xl mx-8">
            <div class="relative w-full">
              <input
                ref="desktopSearchRef"
                v-model="searchQuery"
                type="text"
                :aria-label="t('a11y.search')"
                :placeholder="t('header.searchPlaceholder')"
                class="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                @keyup.enter="handleSearch"
              />
              <UIcon name="i-lucide-search" class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <kbd class="hidden xl:flex absolute right-4 top-1/2 -translate-y-1/2 px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
                ⌘K
              </kbd>
            </div>
          </div>

          <!-- Right Actions -->
          <div class="flex items-center gap-1 sm:gap-2">
            <!-- Search Toggle (Mobile) -->
            <button
              :aria-label="t('a11y.search')"
              :aria-expanded="searchOpen"
              aria-controls="mobile-site-search"
              class="flex size-11 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 lg:hidden"
              @click="searchOpen = !searchOpen"
            >
              <UIcon name="i-lucide-search" class="w-5 h-5" />
            </button>

            <!-- Language Switcher -->
            <UDropdownMenu :items="languageItems">
              <button class="hidden sm:flex items-center gap-1 h-10 px-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                <UIcon name="i-lucide-globe" class="w-4 h-4" />
                <span class="hidden md:inline">{{ currentLocale?.name }}</span>
                <UIcon name="i-lucide-chevron-down" class="w-3 h-3" />
              </button>
            </UDropdownMenu>

            <!-- Dark Mode Toggle -->
            <div class="hidden min-[360px]:block">
              <DarkModeToggle />
            </div>

            <!-- User Menu -->
            <div v-if="isLoggedIn" class="hidden sm:block">
              <UDropdownMenu :items="userMenuItems">
                <button class="flex items-center gap-2 h-10 px-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                  <div class="w-8 h-8 bg-gradient-to-br from-brand-500 to-emerald-600 rounded-full flex items-center justify-center shadow-sm overflow-hidden">
                    <img v-if="user?.avatarUrl" :src="user.avatarUrl" :alt="user?.fullName || ''" class="w-full h-full object-cover" />
                    <span v-else class="text-white text-sm font-semibold">
                      {{ user?.fullName?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U' }}
                    </span>
                  </div>
                  <UIcon name="i-lucide-chevron-down" class="w-4 h-4 text-gray-400" />
                </button>
              </UDropdownMenu>
            </div>

            <!-- Login Button -->
            <NuxtLink
              v-else
              :to="loginRoute"
              class="hidden sm:flex items-center gap-2 h-10 px-4 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <UIcon name="i-lucide-user" class="w-5 h-5" />
              <span class="hidden md:inline">{{ t('nav.login') }}</span>
            </NuxtLink>

            <!-- Cart -->
            <NuxtLink
              :to="localePath('/carrito')"
              :aria-label="t('a11y.cart')"
              class="relative flex size-11 items-center justify-center rounded-xl bg-brand-600 text-white transition-colors hover:bg-brand-700"
            >
              <UIcon name="i-lucide-shopping-cart" class="w-5 h-5" />
              <span
                v-if="cartCount > 0"
                class="absolute -top-1 -right-1 bg-green-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-semibold shadow-lg"
              >
                {{ cartCount }}
              </span>
            </NuxtLink>

            <!-- Mobile menu button -->
            <button
              ref="menuButtonRef"
              :aria-label="t('header.menu')"
              :aria-expanded="mobileMenuOpen"
              aria-controls="mobile-navigation-drawer"
              class="flex size-11 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 lg:hidden"
              @click="mobileMenuOpen = !mobileMenuOpen"
            >
              <UIcon :name="mobileMenuOpen ? 'i-lucide-x' : 'i-lucide-menu'" class="w-6 h-6" />
            </button>
          </div>
        </div>

        <!-- Mobile Search Bar -->
        <Transition name="header-slide-down">
          <div v-if="searchOpen" id="mobile-site-search" class="pb-4 lg:hidden">
            <div class="relative">
              <input
                ref="mobileSearchRef"
                v-model="searchQuery"
                type="text"
                :aria-label="t('a11y.search')"
                :placeholder="t('header.searchPlaceholder')"
                class="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 transition-all"
                @keyup.enter="handleSearch"
              />
              <UIcon name="i-lucide-search" class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Navigation Bar (Desktop) -->
    <nav class="hidden lg:block bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-12">
          <!-- Main Nav Links -->
          <div class="flex items-center gap-1 -ml-4">
            <NuxtLink
              v-for="item in navItems"
              :key="item.label"
              :to="item.to"
              :class="[
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                isActive(item.to)
                  ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
              ]"
            >
              <UIcon :name="item.icon" class="w-4 h-4" />
              {{ item.label }}
            </NuxtLink>
          </div>

          <!-- Quick Links -->
          <div class="flex items-center gap-4 text-sm">
            <NuxtLink
              :to="localePath('/cuenta?tab=orders')"
              class="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <UIcon name="i-lucide-package" class="w-4 h-4" />
              {{ t('nav.trackOrder') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath('/help')"
              class="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <UIcon name="i-lucide-headphones" class="w-4 h-4" />
              {{ t('nav.help') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </nav>

    <!-- Mobile Navigation Drawer -->
    <ClientOnly>
      <Teleport to="body">
        <!-- Backdrop -->
        <Transition name="header-fade">
          <div
            v-if="mobileMenuOpen"
            class="fixed inset-0 bg-black/50 z-40 lg:hidden"
            @click="closeMobileMenu()"
          />
        </Transition>

        <!-- Drawer -->
        <Transition name="header-slide-right">
          <div
            v-if="mobileMenuOpen"
            id="mobile-navigation-drawer"
            ref="drawerRef"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-navigation-title"
            class="fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white dark:bg-gray-900 z-50 lg:hidden overflow-y-auto"
            @keydown="handleDrawerKeydown"
          >
          <!-- Drawer Header -->
          <div class="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
            <span id="mobile-navigation-title" class="text-lg font-bold text-gray-900 dark:text-white">{{ t('header.menu') }}</span>
            <button
              ref="drawerCloseRef"
              :aria-label="t('a11y.close')"
              class="flex size-11 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
              @click="closeMobileMenu()"
            >
              <UIcon name="i-lucide-x" class="w-5 h-5" />
            </button>
          </div>

          <!-- User Section -->
          <div class="p-4 border-b border-gray-100 dark:border-gray-800">
            <div v-if="isLoggedIn" class="space-y-3">
              <div class="flex items-center gap-3">
                <div class="w-12 h-12 bg-gradient-to-br from-brand-500 to-emerald-600 rounded-full flex items-center justify-center overflow-hidden">
                  <img v-if="user?.avatarUrl" :src="user.avatarUrl" :alt="user?.fullName || ''" class="w-full h-full object-cover" />
                  <span v-else class="text-white text-lg font-semibold">
                    {{ user?.fullName?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U' }}
                  </span>
                </div>
                <div>
                  <p class="font-semibold text-gray-900 dark:text-white">{{ user?.fullName || t('nav.user') }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ user?.email }}</p>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-2">
                <NuxtLink
                  :to="localePath('/cuenta')"
                  class="flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300"
                  @click="mobileMenuOpen = false"
                >
                  <UIcon name="i-lucide-user" class="w-4 h-4" />
                  {{ t('nav.myAccount') }}
                </NuxtLink>
                <NuxtLink
                  :to="localePath('/cuenta?tab=orders')"
                  class="flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300"
                  @click="mobileMenuOpen = false"
                >
                  <UIcon name="i-lucide-package" class="w-4 h-4" />
                  {{ t('nav.myOrders') }}
                </NuxtLink>
              </div>
            </div>
            <div v-else>
              <NuxtLink
                :to="loginRoute"
                class="flex items-center justify-center gap-2 w-full px-4 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-xl font-semibold transition-colors"
                @click="mobileMenuOpen = false"
              >
                <UIcon name="i-lucide-user" class="w-5 h-5" />
                {{ t('nav.login') }}
              </NuxtLink>
              <p class="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
                {{ t('header.noAccount') }}
                <NuxtLink :to="registerRoute" class="text-brand-600 dark:text-brand-400 font-medium" @click="mobileMenuOpen = false">
                  {{ t('nav.register') }}
                </NuxtLink>
              </p>
            </div>
          </div>

          <!-- Navigation Links -->
          <div class="p-4">
            <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{{ t('header.navigation') }}</p>
            <div class="space-y-1">
              <NuxtLink
                v-for="item in navItems"
                :key="item.label"
                :to="item.to"
                :class="[
                  'flex items-center gap-3 px-4 py-3 rounded-xl transition-colors',
                  isActive(item.to)
                    ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                ]"
                @click="mobileMenuOpen = false"
              >
                <div :class="[
                  'w-10 h-10 rounded-xl flex items-center justify-center',
                  isActive(item.to) ? 'bg-brand-100 dark:bg-brand-900/30' : 'bg-gray-100 dark:bg-gray-800'
                ]">
                  <UIcon :name="item.icon" class="w-5 h-5" />
                </div>
                <div>
                  <p class="font-medium">{{ item.label }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ item.description }}</p>
                </div>
              </NuxtLink>
            </div>
          </div>

          <!-- Language Selection -->
          <div class="p-4 border-t border-gray-100 dark:border-gray-800">
            <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{{ t('header.language') }}</p>
            <div class="grid grid-cols-2 gap-2">
              <NuxtLink
                v-for="loc in locales"
                :key="loc.code"
                :to="switchLocalePath(loc.code)"
                :class="[
                  'flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  locale === loc.code
                    ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                ]"
                @click="mobileMenuOpen = false"
              >
                {{ loc.name }}
              </NuxtLink>
            </div>
          </div>

          <!-- Logout Button -->
          <div v-if="isLoggedIn" class="p-4 border-t border-gray-100 dark:border-gray-800">
            <button
              class="flex items-center justify-center gap-2 w-full px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl font-medium transition-colors"
              @click="logout(); mobileMenuOpen = false"
            >
              <UIcon name="i-lucide-log-out" class="w-5 h-5" />
              {{ t('nav.logout') }}
            </button>
          </div>
          </div>
        </Transition>
      </Teleport>
    </ClientOnly>
  </header>
</template>
